import { PrinterIcon } from '@nimbus-ds/icons';
import { OrderDetailsResponseDto } from '@tiendanube/common';
import {
  Status as StatusEnum,
  FulfillmentPreferenceType as FulfillmentPreferenceTypeEnum,
} from '@tiendanube/common/src/enums';
import { ActionProp } from 'commons/components';
import {
  useHasPermission,
  useHasUnifiedShippingDocumentsEnabled,
} from 'domains/Auth/hooks';
import {
  DocumentType,
  FULFILLMENT_ORDER_ID_IDENTIFIER,
} from 'domains/FulfillmentOrders/pages/PrintDocumentPage/constants';
import { useGoToPrintPage } from 'domains/FulfillmentOrders/pages/PrintDocumentPage/hooks';
import { trackingOrderDetailsButtonPrintLabelClick } from 'domains/Orders/tracking';
import { isShippingMethodOca } from '../../components/EditDeliveryAdressModal/utils';

const getActionLabel = (
  isUnifiedEnabled: boolean,
  unifiedKey: string,
  legacyKey: string,
  t,
) => t(isUnifiedEnabled ? unifiedKey : legacyKey);

const buildMultipleLocationActions = (params) => {
  const {
    shippableFulfillmentOrders,
    isUnifiedEnabled,
    documentType,
    t,
    goToPrintPage,
    orderDetailsId,
  } = params;

  const locationActions = shippableFulfillmentOrders.map(
    (fulfillmentOrder) => ({
      children: t(
        isUnifiedEnabled
          ? 'detail.printShippingDocumentPopover'
          : 'detail.printLabelPopover',
        { location: fulfillmentOrder.assignedLocation.name },
      ),
      onClick: () => {
        trackingOrderDetailsButtonPrintLabelClick(orderDetailsId);
        goToPrintPage({
          ids: [`${FULFILLMENT_ORDER_ID_IDENTIFIER}${fulfillmentOrder.id}`],
          type: documentType,
        });
      },
      icon: PrinterIcon,
      name: 'printLabel',
    }),
  );

  return [
    ...locationActions,
    {
      children: getActionLabel(
        isUnifiedEnabled,
        'detail.printShippingDocumentAll',
        'detail.printLabelAll',
        t,
      ),
      onClick: () => {
        trackingOrderDetailsButtonPrintLabelClick(orderDetailsId);
        goToPrintPage({
          ids: [orderDetailsId],
          type: documentType,
        });
      },
      icon: PrinterIcon,
      name: 'printLabel',
    },
  ];
};

const buildSingleLocationAction = (params): ActionProp => {
  const {
    shippableFulfillmentOrders,
    isUnifiedEnabled,
    documentType,
    t,
    goToPrintPage,
    orderDetailsId,
    isOca,
    shouldGenerateOcaTag,
  } = params;

  const [fulfillmentOrder] = shippableFulfillmentOrders;

  return {
    children: getActionLabel(
      isUnifiedEnabled,
      'detail.printShippingDocument',
      'detail.printLabel',
      t,
    ),
    onClick: () => {
      trackingOrderDetailsButtonPrintLabelClick(orderDetailsId);
      goToPrintPage({
        ids:
          shippableFulfillmentOrders.length === 1 && !isOca
            ? [`${FULFILLMENT_ORDER_ID_IDENTIFIER}${fulfillmentOrder.id}`]
            : [orderDetailsId],
        type: documentType,
        hasOca: isOca,
        hasOcaAndNotGeneratedTag: shouldGenerateOcaTag(),
      });
    },
    icon: PrinterIcon,
    appearance: 'primary' as const,
    name: 'printLabel',
  };
};

const processShippableOrders = (params): ActionProp[] => {
  const {
    fulfillmentOrders,
    isDigital,
    isUnifiedShippingDocumentsEnabled,
    documentType,
    t,
    goToPrintPage,
    orderDetailsId,
    orderDetailsShipping,
    orderDetailsFulfillment,
  } = params;

  const shouldGenerateOcaTag = () =>
    isShippingMethodOca(orderDetailsShipping) &&
    orderDetailsFulfillment?.preference?.deliveryAddress?.hasGeneratedTag ===
      false;
  const isOca = isShippingMethodOca(orderDetailsShipping);

  const shippableFulfillmentOrders = fulfillmentOrders.filter(
    (fulfillmentOrder) =>
      fulfillmentOrder?.shipping?.type !==
      FulfillmentPreferenceTypeEnum.NON_SHIPPABLE,
  );

  if (shippableFulfillmentOrders.length > 1 && !isOca && !isDigital) {
    return buildMultipleLocationActions({
      shippableFulfillmentOrders,
      isUnifiedEnabled: isUnifiedShippingDocumentsEnabled,
      documentType,
      t,
      goToPrintPage,
      orderDetailsId,
    });
  } else if (!isDigital) {
    return [
      buildSingleLocationAction({
        shippableFulfillmentOrders,
        isUnifiedEnabled: isUnifiedShippingDocumentsEnabled,
        documentType,
        t,
        goToPrintPage,
        orderDetailsId,
        isOca,
        shouldGenerateOcaTag,
      }),
    ];
  }

  return [];
};

export const useBuildPrintLabelAction = (
  orderDetails: OrderDetailsResponseDto | null,
  t,
): ActionProp[] => {
  const printLabelAction: ActionProp[] = [];
  const hasPrintShippingLabelPermission = useHasPermission(
    'print_order_shipping_labels',
  );
  const { goToPrintPage } = useGoToPrintPage();
  const isUnifiedShippingDocumentsEnabled =
    useHasUnifiedShippingDocumentsEnabled();

  if (!orderDetails) {
    return printLabelAction;
  }

  const fulfillmentOrders = orderDetails?.fulfillmentOrders || [];

  const isDigital =
    orderDetails.fulfillment?.preference?.type ===
    FulfillmentPreferenceTypeEnum.DELIVERY_DIGITAL;

  const documentType = isUnifiedShippingDocumentsEnabled
    ? DocumentType.SHIPPING_DOCUMENT
    : DocumentType.LABEL;

  if (
    orderDetails?.status === StatusEnum.OPEN &&
    hasPrintShippingLabelPermission
  ) {
    const actions = processShippableOrders({
      fulfillmentOrders,
      isDigital,
      isUnifiedShippingDocumentsEnabled,
      documentType,
      t,
      goToPrintPage,
      orderDetailsId: orderDetails.id,
      orderDetailsShipping: orderDetails.shipping,
      orderDetailsFulfillment: orderDetails.fulfillment,
    });
    printLabelAction.push(...actions);
  }
  return printLabelAction;
};

export default useBuildPrintLabelAction;
